import React from "react";
import { useTranslation } from "react-i18next";
import {
  ArchiveBoxXMarkIcon,
  ArrowPathIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ClockIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import KaTeXComponent from "../../../components/KaTeXComponent/KaTeXComponent";
import { DifficultyBadge } from "../../../components/Difficulty/Difficulty";
import PassagePanel from "../../../components/Passages/PassagePanel";
import { statusTone } from "../../../components/QuestionList/questionStatus";
import { cardClass } from "../../Exams/TestBuilder/ui";
import { statusLabel } from "./reviewUtils";
import VoteBar from "./VoteBar";

// The icon of each status; the colors are the bank's (statusTone).
export const STATUS_ICONS = {
  DRAFT: PencilSquareIcon,
  PENDING_REVIEW: ClockIcon,
  UNDER_REVIEW: MagnifyingGlassIcon,
  CHANGES_REQUESTED: PencilIcon,
  RESUBMITTED: ArrowPathIcon,
  APPROVED: CheckIcon,
  DECLINED: XMarkIcon,
  ARCHIVED: ArchiveBoxXMarkIcon,
};

// A question's status ({code, name}) by its code: its label, color and icon.
export function QuestionStatusPill({ status, size = "md" }) {
  const { t } = useTranslation();
  if (!status?.code && !status?.name) return null;
  const Icon = STATUS_ICONS[status.code] || ClockIcon;
  const small = size === "sm";
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full font-medium ring-1 ring-inset ${statusTone(status.code).badge} ${
        small ? "gap-1 px-2 py-0.5 text-xs" : "gap-1.5 px-3 py-1 text-sm"
      }`}
    >
      <Icon className={small ? "h-3 w-3" : "h-4 w-4"} aria-hidden="true" />
      {statusLabel(t, status)}
    </span>
  );
}

function VoteSummary({ question }) {
  const { t } = useTranslation();
  const upvotes = question.up_count || 0;
  const downvotes = question.down_count || 0;
  const total = upvotes + downvotes;
  const netScore = upvotes - downvotes;
  return (
    <div className="rounded-xl border border-slate-200 p-4 sm:p-5">
      <h4 className="mb-4 flex items-center gap-2.5 text-sm font-semibold text-slate-900">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <ChartBarIcon className="h-4 w-4" aria-hidden="true" />
        </span>
        {t("question_detail.summary.title")}
      </h4>
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-sm text-emerald-700 ring-1 ring-inset ring-emerald-600/10">
            <HandThumbUpIcon className="h-4 w-4" aria-hidden="true" />
            <span className="font-semibold tabular-nums">{upvotes}</span>
            <span>{t("question_detail.summary.upvotes")}</span>
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5 text-sm text-red-700 ring-1 ring-inset ring-red-600/10">
            <HandThumbDownIcon className="h-4 w-4" aria-hidden="true" />
            <span className="font-semibold tabular-nums">{downvotes}</span>
            <span>{t("question_detail.summary.downvotes")}</span>
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 px-3 py-1.5 text-sm text-slate-700 ring-1 ring-inset ring-slate-500/10">
            <ChatBubbleLeftRightIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
            <span className="font-semibold tabular-nums">{total}</span>
            <span>{t("question_detail.summary.total_reviews", { count: total })}</span>
          </div>
        </div>
        <div className="flex items-center text-sm">
          <span className="text-slate-500">{t("question_detail.summary.net_score")} </span>
          <span className={`ml-1 font-semibold tabular-nums ${netScore >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {netScore >= 0 ? "+" : ""}
            {netScore}
          </span>
          <span
            className={`ml-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
              netScore >= 0 ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20" : "bg-red-50 text-red-700 ring-red-600/20"
            }`}
          >
            {netScore >= 0 ? t("question_detail.summary.positive") : t("question_detail.summary.negative")}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The question itself: its code, subject, difficulty, purpose and status, its subtopic and objective, its
 * passage, its text with the answer options (the correct one marked) and, with `showVotes`, the experts' votes
 * once there are any (a subject's questions get none: their review has no votes). `titleExtra` goes next to the
 * title (the user's own vote).
 */
export default function QuestionCard({ question, showVotes = false, titleExtra = null, canVote = false, onVoted }) {
  const { t } = useTranslation();
  return (
    <div className={cardClass}>
      <div className="space-y-5 p-5 sm:p-6">
        <div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                  {t("question_detail.question_id", { code: question.code })}
                </h2>
                {titleExtra}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {question.subject?.name && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                    <BookOpenIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    {question.subject.name}
                  </span>
                )}
                <DifficultyBadge level={question.difficulty} withLabel />
                {question.purpose?.name && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-500/20">
                    <CalendarDaysIcon className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
                    {question.purpose.name}
                  </span>
                )}
              </div>
            </div>
            {(question.status?.code || question.status?.name) && (
              <div className="flex shrink-0 flex-col-reverse items-start gap-1 sm:items-end">
                <QuestionStatusPill status={question.status} />
                <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("question_detail.status")}</span>
              </div>
            )}
          </div>

          {(question.subtopic?.name || question.objective?.name) && (
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-slate-100 pt-4 text-sm">
              {question.subtopic?.name && (
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-medium text-slate-500">{t("question_detail.subtopic")}</span>
                  <span className="text-slate-700">{question.subtopic.name}</span>
                </div>
              )}
              {question.objective?.name && (
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-medium text-slate-500">{t("question_detail.objective")}</span>
                  <span className="text-slate-700">{question.objective.name}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <PassagePanel passage={question.passage} defaultOpen />

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
          <h3 className="mb-4 text-base font-medium leading-relaxed text-slate-900">
            <KaTeXComponent text={question.body} />
          </h3>
          <div className="space-y-2">
            {question.answer_keys?.map((opt) => (
              <div key={opt.uuid} className="flex items-center gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-inset ring-slate-200">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                    opt.is_correct
                      ? "bg-blue-600 text-white shadow-sm shadow-blue-600/25"
                      : "bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200"
                  }`}
                >
                  {opt.option}
                </span>
                <span className="min-w-0 text-slate-700">
                  <KaTeXComponent text={opt.body} />
                </span>
              </div>
            ))}
          </div>
        </div>

        {canVote ? (
          <VoteBar question={question} onVoted={onVoted} />
        ) : (
          showVotes && (question.up_count || 0) + (question.down_count || 0) > 0 && <VoteSummary question={question} />
        )}
      </div>
    </div>
  );
}
